import { useState, useEffect, useRef, useCallback } from "react";

const API_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tiktok-search`;
const API_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const fetchApi = async (params: Record<string, string>) => {
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${API_BASE}?${qs}`, {
    headers: { "Content-Type": "application/json", apikey: API_KEY },
  });
  return res.json();
};

// --- Search Users ---
export interface TikTokUser {
  uid: string;
  unique_id: string;
  nickname: string;
  avatar: string;
  follower_count: number;
}

export function useTikTokSearch(query: string) {
  const [users, setUsers] = useState<TikTokUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.replace(/^@/, "").trim();
    if (trimmed.length < 2) {
      setUsers([]);
      setSearched(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const json = await fetchApi({ action: "search", keyword: trimmed, count: "10" });
        setUsers(json?.users || []);
      } catch {
        setUsers([]);
      } finally {
        setLoading(false);
        setSearched(true);
      }
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  return { users, loading, searched };
}

// --- Get User Details ---
export interface TikTokUserDetail {
  uid: string;
  sec_uid: string;
  unique_id: string;
  nickname: string;
  avatar: string;
  avatar_large: string;
  follower_count: number;
  following_count: number;
  total_favorited: number;
  aweme_count: number;
  signature: string;
}

export function useTikTokUser(username: string) {
  const [user, setUser] = useState<TikTokUserDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchUser = useCallback(async (uname: string) => {
    if (!uname) return;
    setLoading(true);
    try {
      const json = await fetchApi({ action: "user", username: uname });
      setUser(json?.user || null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (username) fetchUser(username);
  }, [username, fetchUser]);

  return { user, loading, refetch: fetchUser };
}

// --- Get User Posts ---
export interface TikTokPost {
  id: string;
  desc: string;
  create_time: number;
  cover: string;
  play_url: string;
  duration: number;
  play_count: number;
  digg_count: number;
  comment_count: number;
  share_count: number;
}

export function useTikTokPosts(secUid: string) {
  const [posts, setPosts] = useState<TikTokPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const cursorRef = useRef<string>("0");

  useEffect(() => {
    if (!secUid) return;
    setPosts([]);
    cursorRef.current = "0";
    setLoading(true);
    fetchApi({ action: "posts", sec_uid: secUid, count: "6", cursor: "0" })
      .then((json) => {
        setPosts(json?.posts || []);
        setHasMore(json?.has_more || false);
        cursorRef.current = String(json?.cursor || json?.posts?.length || "0");
      })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [secUid]);

  const loadMore = useCallback(async () => {
    if (!secUid || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const json = await fetchApi({ action: "posts", sec_uid: secUid, count: "6", cursor: cursorRef.current });
      const newPosts = json?.posts || [];
      setPosts((prev) => {
        const ids = new Set(prev.map(p => p.id));
        return [...prev, ...newPosts.filter((p: TikTokPost) => !ids.has(p.id))];
      });
      setHasMore(json?.has_more || false);
      cursorRef.current = String(json?.cursor || "0");
    } catch {
      // ignore
    } finally {
      setLoadingMore(false);
    }
  }, [secUid, loadingMore, hasMore]);

  return { posts, loading, hasMore, loadingMore, loadMore };
}
